import type { DataItem } from '@pn/core/domain/data';
import type { IntelReport } from '@pn/core/domain/intel/intel-report';
import type { IntelDataItem } from '@pn/core/domain/intel/intel-workspace-item';
import { useBuildSliceSelector } from '@pn/services/redux/useBuildSliceSelector';
import { createSelector } from '@reduxjs/toolkit';
import { isEmpty, isNil } from 'lodash-es';
import { store } from 'src/application/storage/store';
import { intelReportsSlice, type IntelReportsState } from './intelReportsSlice';

export const useIntelReportsStorage = () => {
  const { name, getInitialState } = intelReportsSlice;

  const useSliceSelector = useBuildSliceSelector(name, getInitialState());

  return {
    isFetching: useSliceSelector((state) => state.isFetching),
    isReportActive: useSliceSelector(
      (state) => !isEmpty(state.selectedReportId)
    ),
    isEditingReport: useSliceSelector((state) => state.isEditingReport),
    reports: useSliceSelector((state) => state.reports),
    pinnedReports: useSliceSelector(selectPinnedReports),
    selectedReport: useSliceSelector(selectSelectedReport),
    selectedReportWithData: useSliceSelector(selectSelectedReportWithData),
    selectedReportData: useSliceSelector(selectSelectedReportData),
    selectedReportId: useSliceSelector<string | undefined>(
      (state) => state.selectedReportId
    ),
    licences: useSliceSelector(selectLicenses),
    spuds: useSliceSelector(selectSpuds),
    reportToLoad: useSliceSelector(selectReportToLoad),
  };
};

export const intelReportsActions = () => {
  const { dispatch, getState } = store;
  const { actions, name } = intelReportsSlice;

  if (isNil(getState()[name])) {
    throw new Error(`[${name}] reducer has not been injected yet`);
  }

  return {
    request: () => {
      dispatch(actions.request());
    },
    receive: (reports: IntelReport[]) => {
      dispatch(actions.receive(reports));
    },
    toggleIsEditingReport: () => {
      dispatch(actions.toggleIsEditingReport());
    },
    create: (params: IntelReport) => {
      dispatch(actions.create(params));
    },
    update: (reportId: IntelReport['id'], updatedReport: IntelReport) => {
      dispatch(actions.update({ reportId, updatedReport }));
    },
    updateEmailSchedule(
      reportId: IntelReport['id'],
      emailFrequency: IntelReport['emailSchedule']
    ) {
      dispatch(actions.updateEmailSchedule({ reportId, emailFrequency }));
    },
    updateIsPinned(reportId: IntelReport['id'], isPinned: boolean) {
      dispatch(actions.updateIsPinned({ reportId, isPinned }));
    },
    saveSharedReport: (oldReportId: string, savedReport: IntelReport) => {
      dispatch(actions.saveSharedReport({ oldReportId, savedReport }));
    },
    selectReport: (id: IntelReport['id']) => {
      dispatch(actions.selectReport(id));
    },
    remove(reportId: IntelReport['id']) {
      dispatch(actions.remove(reportId));
    },
    setProduction(reportId: IntelReport['id'], production: DataItem[]) {
      dispatch(actions.setProduction({ reportId, production }));
    },
    setYearOverYearData: (
      reportId: IntelReport['id'],
      spudsYearOverYear: IntelDataItem[],
      licencesYearOverYear: IntelDataItem[]
    ) => {
      dispatch(
        actions.setYearOverYearData({
          reportId,
          spudsYearOverYear,
          licencesYearOverYear,
        })
      );
    },
    setMonthlyLicences(
      reportId: IntelReport['id'],
      monthlyLicences: DataItem[]
    ) {
      dispatch(actions.setMonthlyLicences({ reportId, monthlyLicences }));
    },
    setMonthlySpuddedLicenses: (
      reportId: IntelReport['id'],
      monthlySpuddedLicenses: DataItem[]
    ) => {
      dispatch(
        actions.setMonthlySpuddedLicenses({ reportId, monthlySpuddedLicenses })
      );
    },
    setIsLoadingProduction: (reportId: IntelReport['id']) => {
      dispatch(actions.setIsLoadingProduction(reportId));
    },
    setIsLoadingWells(reportId: IntelReport['id']) {
      dispatch(actions.setIsLoadingWells(reportId));
    },
    markReportAsLoaded(id: IntelReport['id']) {
      dispatch(actions.markReportAsLoaded(id));
    },
    unselectReport() {
      dispatch(actions.unselectReport());
    },
    error() {
      dispatch(actions.error());
    },
  };
};

const selectPinnedReports = createSelector(
  [(state: IntelReportsState) => state.reports],
  (reports) => reports.filter((r) => r.isPinned)
);

const selectSelectedReport = createSelector(
  [
    (state: IntelReportsState) => state.reports,
    (state: IntelReportsState) => state.selectedReportId,
  ],
  (reports, selectedReportId) =>
    reports.find((c) => selectedReportId.includes(c.id))
);

const selectSelectedReportWithData = createSelector(
  [
    (state: IntelReportsState) => state.reports,
    (state: IntelReportsState) => state.reportsData,
    (state: IntelReportsState) => state.selectedReportId,
  ],
  (reports, reportsData, selectedReportId) => {
    const report = reports.find((c) => selectedReportId.includes(c.id));

    const reportData = reportsData.find((c) =>
      selectedReportId.includes(c.reportId)
    );

    if (isNil(report) || isNil(reportData)) return;
    return { ...report, ...reportData };
  }
);

const selectSelectedReportData = createSelector(
  [
    (state: IntelReportsState) => state.reportsData,
    (state: IntelReportsState) => state.selectedReportId,
  ],
  (reportsData, selectedReportId) => {
    const reportData = reportsData.find((c) =>
      selectedReportId.includes(c.reportId)
    );

    if (isNil(reportData)) return;
    return reportData;
  }
);

const selectLicenses = createSelector(
  [
    (state: IntelReportsState) => state.reportsData,
    (state: IntelReportsState) => state.selectedReportId,
  ],
  (reportsData, selectedReportId) => {
    const reportData = reportsData.find((c) =>
      selectedReportId.includes(c.reportId)
    );

    if (isNil(reportData)) return [];
    return (reportData.monthlySpuddedLicenses ?? []).filter(
      (c) => c['licence_date'] === 'licence'
    );
  }
);

const selectSpuds = createSelector(
  [
    (state: IntelReportsState) => state.reportsData,
    (state: IntelReportsState) => state.selectedReportId,
  ],
  (reportsData, selectedReportId) => {
    const reportData = reportsData.find((rd) =>
      selectedReportId.includes(rd.reportId)
    );
    if (isNil(reportData)) return [];
    return reportData.monthlyLicenses ?? [];
  }
);

const selectReportToLoad = createSelector(
  [
    (state: IntelReportsState) => state.reports,
    (state: IntelReportsState) => state.reportsData,
    (state: IntelReportsState) => state.selectedReportId,
    (state: IntelReportsState) => state.loadedReportIds,
  ],
  (reports, reportsData, selectedReportId, loadedReportIds) => {
    const loadingReport = reportsData.find((r) => r.isLoading);
    if (!isNil(loadingReport)) return; // prevents loading multiple reports at a time
    return reports.find(
      (r) => selectedReportId.includes(r.id) && !loadedReportIds.includes(r.id)
    );
  }
);
